// shared-types/src/storeCustomization/normalizeLayout.ts
import type { StoreCustomizationLayout, StoreCustomizationLayoutComponent } from './types'
import { isZonedLayout } from './types'

const emptyZoneComponents = {
    preComponents: [] as StoreCustomizationLayoutComponent[],
    postComponents: [] as StoreCustomizationLayoutComponent[],
}

export function createEmptyStoreCustomizationLayout(): StoreCustomizationLayout {
    return {
        headerZone: {
            header: null as unknown as StoreCustomizationLayoutComponent,
            ...emptyZoneComponents,
        },
        footerZone: {
            footer: null as unknown as StoreCustomizationLayoutComponent,
            preComponents: [],
            postComponents: [],
        },
    }
}

function copyZonedLayout(zoned: StoreCustomizationLayout): StoreCustomizationLayout {
    return {
        headerZone: {
            header: (zoned.headerZone?.header ?? null) as StoreCustomizationLayoutComponent,
            preComponents: Array.isArray(zoned.headerZone?.preComponents)
                ? zoned.headerZone.preComponents
                : [],
            postComponents: Array.isArray(zoned.headerZone?.postComponents)
                ? zoned.headerZone.postComponents
                : [],
        },
        footerZone: {
            footer: (zoned.footerZone?.footer ?? null) as StoreCustomizationLayoutComponent,
            preComponents: Array.isArray(zoned.footerZone?.preComponents)
                ? zoned.footerZone.preComponents
                : [],
            postComponents: Array.isArray(zoned.footerZone?.postComponents)
                ? zoned.footerZone.postComponents
                : [],
        },
    }
}

function migrateLegacyLayoutComponents(components: unknown[]): StoreCustomizationLayout {
    const list = Array.isArray(components) ? components : []
    const header =
        (list.find((c: any) => c?.type === 'header') as StoreCustomizationLayoutComponent | undefined) ??
        (null as unknown as StoreCustomizationLayoutComponent)
    const footer =
        (list.find((c: any) => c?.type === 'footer') as StoreCustomizationLayoutComponent | undefined) ??
        (null as unknown as StoreCustomizationLayoutComponent)
    const preHeaderComponents = list.filter(
        (c: any) => c?.type === 'announcement-bar'
    ) as StoreCustomizationLayoutComponent[]

    return {
        headerZone: {
            header,
            preComponents: preHeaderComponents,
            postComponents: [],
        },
        footerZone: {
            footer,
            preComponents: [],
            postComponents: [],
        },
    }
}

/** True when layout is legacy-only (`components[]` without zones). */
export function isLegacyStoreCustomizationLayout(layout: unknown): boolean {
    return !!(
        layout &&
        typeof layout === 'object' &&
        Array.isArray((layout as { components?: unknown[] }).components) &&
        !isZonedLayout(layout)
    )
}

/**
 * Accepts only zoned layouts. Strips stray legacy `components` from the payload.
 * Returns null for legacy-only or invalid input (must not be persisted as-is).
 */
export function sanitizeStoreCustomizationLayout(layout: unknown): StoreCustomizationLayout | null {
    if (!layout || !isZonedLayout(layout)) {
        return null
    }
    return copyZonedLayout(layout as StoreCustomizationLayout)
}

/**
 * Upgrades legacy `{ components[] }` from old DB rows or templates to zoned layout.
 * Use on read paths only — never persist the legacy shape again.
 */
export function upgradeLegacyStoreCustomizationLayout(layout: unknown): StoreCustomizationLayout {
    const sanitized = sanitizeStoreCustomizationLayout(layout)
    if (sanitized) {
        return sanitized
    }

    if (layout && typeof layout === 'object' && Array.isArray((layout as { components?: unknown[] }).components)) {
        return migrateLegacyLayoutComponents((layout as { components: unknown[] }).components)
    }

    return createEmptyStoreCustomizationLayout()
}

/** @deprecated Use sanitizeStoreCustomizationLayout (write) or upgradeLegacyStoreCustomizationLayout (read). */
export function normalizeStoreCustomizationLayout(layout: unknown): StoreCustomizationLayout {
    return upgradeLegacyStoreCustomizationLayout(layout)
}

export function resolveStoreCustomizationLayoutForRead(layout: unknown): StoreCustomizationLayout {
    return upgradeLegacyStoreCustomizationLayout(layout)
}

export function resolveStoreCustomizationLayoutForWrite(layout: unknown): StoreCustomizationLayout | null {
    return sanitizeStoreCustomizationLayout(layout)
}
